export interface DocumentListFilters {
  accountId: string;
  dateFrom: string;
  dateTo: string;
  page: number;
  search: string;
  status: string;
}

interface DocumentQueryKeys {
  accountId: string;
  dateFrom: string;
  dateTo: string;
  page: string;
  search: string;
  status: string;
}

const DEFAULT_KEYS: DocumentQueryKeys = {
  search: "q",
  accountId: "account_id",
  status: "status",
  dateFrom: "date_from",
  dateTo: "date_to",
  page: "page",
};

const MAX_PAGE = 10_000;

export function setOptionalParam(sp: URLSearchParams, key: string, value: string | undefined) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    sp.delete(key);
    return;
  }
  sp.set(key, trimmed);
}

export function readDocumentFiltersFromSearchParams(
  searchParams: URLSearchParams,
  keys: DocumentQueryKeys = DEFAULT_KEYS,
): DocumentListFilters {
  const search = searchParams.get(keys.search)?.trim() ?? "";
  const accountId = searchParams.get(keys.accountId)?.trim() ?? "";
  const status = searchParams.get(keys.status)?.trim() ?? "";
  const dateFrom = searchParams.get(keys.dateFrom)?.trim() ?? "";
  const dateTo = searchParams.get(keys.dateTo)?.trim() ?? "";

  const pageRaw = searchParams.get(keys.page);
  const pageParsed = pageRaw ? Number.parseInt(pageRaw, 10) : 1;
  const page = Number.isFinite(pageParsed) && pageParsed > 0 ? Math.min(pageParsed, MAX_PAGE) : 1;

  return { search, accountId, status, dateFrom, dateTo, page };
}

export function writeDocumentFiltersToSearchParams(
  previous: URLSearchParams,
  next: Partial<DocumentListFilters>,
  keys: DocumentQueryKeys = DEFAULT_KEYS,
): URLSearchParams {
  const sp = new URLSearchParams(previous);

  if ("search" in next) {
    setOptionalParam(sp, keys.search, next.search);
  }
  if ("accountId" in next) {
    setOptionalParam(sp, keys.accountId, next.accountId);
  }
  if ("status" in next) {
    setOptionalParam(sp, keys.status, next.status);
  }
  if ("dateFrom" in next) {
    setOptionalParam(sp, keys.dateFrom, next.dateFrom);
  }
  if ("dateTo" in next) {
    setOptionalParam(sp, keys.dateTo, next.dateTo);
  }

  if ("page" in next && next.page !== undefined) {
    if (next.page <= 1) {
      sp.delete(keys.page);
    } else {
      sp.set(keys.page, String(next.page));
    }
  }

  return sp;
}
